import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Award, TrendingDown, Euro, Timer, Wrench, Cpu } from "lucide-react";
import { SOLUTION_PAGES } from "@/data/solutionPages";

/**
 * SolutionLanding — Page landing dédiée pour chaque mot-clé SEO cible.
 * Route : /solutions/:slug
 */

export default function SolutionLanding() {
  const { slug } = useParams();
  const page = SOLUTION_PAGES[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (page) {
      document.title = `${page.title} — ECONATION`;
      const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
      meta.setAttribute("name", "description");
      meta.setAttribute("content", page.subtitle);
      document.head.appendChild(meta);
    }
  }, [slug, page]);

  if (!page) return <Navigate to="/solutions" replace />;

  return (
    <div data-testid={`solution-landing-${slug}`} className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={page.heroImage} alt={page.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1A]/95 via-[#0A0F1A]/85 to-[#0A0F1A]/50" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <Link to="/solutions" data-testid="back-to-solutions" className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-600 hover:text-slate-900 transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" /> Toutes nos solutions
          </Link>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] uppercase text-[#10B981] mb-4 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200">
              <Award className="h-3.5 w-3.5" /> Fiche {page.ficheCEE}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.03]">
              {page.title}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">
              {page.subtitle}
            </p>

            {/* Key metrics */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-2xl bg-slate-50 backdrop-blur-md border border-slate-200">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[#10B981]">
                  <Euro className="h-3.5 w-3.5" /> Prime CEE
                </div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{page.primeRange}</div>
              </div>
              <div className="p-4 rounded-2xl bg-slate-50 backdrop-blur-md border border-slate-200">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[#0055A4]">
                  <TrendingDown className="h-3.5 w-3.5" /> Économie
                </div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{page.saving}</div>
              </div>
              <div className="p-4 rounded-2xl bg-slate-50 backdrop-blur-md border border-slate-200">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[#0EA5E9]">
                  <Timer className="h-3.5 w-3.5" /> ROI
                </div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1">{page.payback}</div>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="cta-audit" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0055A4] hover:bg-[#003D7A] text-slate-900 font-semibold text-sm transition-colors">
                Étude gratuite sous 48 h <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#estimation" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-100 hover:bg-slate-100 text-slate-900 font-semibold text-sm transition-colors">
                Estimer ma prime
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* KEY POINTS */}
      <section className="bg-[#F8FAFC] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Points clés</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
                Ce que couvre la fiche {page.ficheCEE}.
              </h2>
              <p className="mt-6 text-[#475569] md:text-lg leading-relaxed">
                Nos ingénieurs vérifient chaque condition d&apos;éligibilité et vous garantissent un dossier accepté PNCEE.
              </p>
            </div>
            <ul className="space-y-3">
              {page.keyPoints.map((k, i) => (
                <li key={i} data-testid={`keypoint-${i}`} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-black/[0.05]">
                  <CheckCircle2 className="h-5 w-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <span className="text-[15px] text-[#334155] leading-relaxed">{k}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* EQUIPMENTS COVERED */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Équipements couverts</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
              Nos ingénieurs interviennent sur ces marques.
            </h2>
            <p className="mt-6 text-[#475569] md:text-lg leading-relaxed">
              Un directeur maintenance reconnaît son parc en un coup d&apos;œil — nous parlons votre langage technique.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {page.equipment.map((brand) => (
              <span
                key={brand}
                data-testid={`brand-${brand}`}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#F8FAFC] border border-black/[0.06] hover:border-[#10B981]/40 transition-colors"
              >
                <Wrench className="h-4 w-4 text-[#0F172A]" />
                <span className="font-semibold text-[#0F172A]">{brand}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-slate-50 text-slate-900 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase mb-5">Notre méthode</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-[1.05]">
              4 étapes clés pour votre projet.
            </h2>
          </div>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {page.process.map((step, i) => (
              <li key={i} className="p-6 rounded-2xl bg-slate-50 border border-slate-200 backdrop-blur-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-8 w-8 rounded-full bg-[#10B981] grid place-items-center text-slate-900 font-display font-bold text-sm">
                    {i + 1}
                  </div>
                  <Cpu className="h-4 w-4 text-slate-400" />
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section id="estimation" className="bg-gradient-to-br from-[#10B981] to-[#064E3B] py-24 md:py-32 text-slate-900">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-[11px] font-semibold tracking-[0.28em] text-slate-600 uppercase mb-5">Passez à l&apos;action</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]">
            Chiffrez votre prime {page.keyword} sous 48 h.
          </h2>
          <p className="mt-6 text-slate-700 md:text-lg leading-relaxed">
            Un ingénieur ECONATION analyse votre installation, calcule le montant exact
            de votre prime CEE et livre l&apos;étude technique offerte.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="footer-cta-contact" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white hover:bg-black text-slate-900 font-semibold text-sm transition-colors">
              Demander mon étude gratuite <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/blog" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-100 hover:bg-white/25 backdrop-blur-md text-slate-900 font-semibold text-sm transition-colors">
              Lire nos articles techniques
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
